import type { UserLocalStorage } from '../../types/localStorage';

import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import useLocalStorageState from 'use-local-storage-state';

import {
  synchronizationExport,
  synchronizationSynchronize,
} from '../../lib/flotiq-client';
import { synchronizationImportZip } from '../../lib/flotiq-client/api-helpers';
import {
  parseResponseErrors,
  ResponseError,
} from '../../lib/flotiq-client/response-errors';

import { defaultButtonsProps, useModals } from '../../contexts/ModalContext';

import { useUser } from '../api';
import useToken from '../useToken';
import ExportSpaceConfirmationModalContent from './modal/ExportSpaceConfirmationModalContent';
import ImportSpaceModalContent from './modal/ImportSpaceModalContent';
import SynchronizeSpaceModalContent from './modal/SynchronizeSpaceModalContent';

export const spaceSynchronizationModalId = 'export-space-confirmation-modal';

export const useSpaceSynchronization = (spaceId?: string, testId?: string) => {
  const modal = useModals();
  const jwt = useToken();
  const [userLS] = useLocalStorageState<UserLocalStorage>('cms.user');
  const [user] = useUser(userLS?.data?.id);

  const { t } = useTranslation();

  const isSpaceAvailable =
    (user?.spaces?.filter((s) => s.id === spaceId) || []).length > 0;

  const handleImport = async (
    mode: 'importSpace' | 'importCTD',
    spaceName?: string,
  ) => {
    if (!spaceId || !jwt) {
      return;
    }

    const titleContent =
      mode === 'importCTD'
        ? t('SpaceSynchronization.Import.ImportCtd.Title')
        : t('SpaceSynchronization.Import.ImportSpace.Title', { spaceName });

    const handleSynchronizationImport = async (value: { files: File[] }) => {
      const { ok, status } = await synchronizationImportZip(
        jwt,
        spaceId,
        value,
        t,
      );
      if (ok) {
        if (status === 202) {
          toast.success(t('SpaceSynchronization.Import.ImportInProgress'));
        } else {
          toast.success(t('SpaceSynchronization.Import.ImportStartedMessage'));
        }
      }

      return ok;
    };

    return modal({
      id: spaceSynchronizationModalId,
      title: <span className="text-3xl font-bold">{titleContent} </span>,
      size: 'lg',
      content: (
        <ImportSpaceModalContent
          onSubmit={handleSynchronizationImport}
          mode={mode}
          testId={testId}
        />
      ),
      dialogAdditionalClasses: 'text-slate-900 text-center',
      contentAdditionalClasses: 'text-center',
    });
  };

  const handleExport = async (
    mode: 'exportSpace' | 'exportCTD',
    name: string,
  ) => {
    if (!spaceId || !jwt) {
      return;
    }

    const titleContent =
      mode === 'exportCTD'
        ? t('SpaceSynchronization.Export.ExportCtd.Title')
        : t('SpaceSynchronization.Export.ExportSpace.Title', {
            spaceName: name,
          });

    const result = await modal({
      id: 'export-space-confirmation-modal',
      title: <span className="text-3xl font-bold">{titleContent}</span>,
      size: 'lg',
      content: <ExportSpaceConfirmationModalContent mode={mode} />,
      dialogAdditionalClasses: 'text-bold text-slate-900 text-center',
      contentAdditionalClasses: 'text-center',
      buttons: [
        {
          ...defaultButtonsProps.cancel,
          label: t('Global.Cancel'),
          result: false,
        },
        {
          ...defaultButtonsProps.ok,
          label: t('SpaceSynchronization.Export.StartExportButton'),
          result: true,
        },
      ],
    });

    if (!result) {
      return;
    }

    try {
      const { body, status, ok } = await synchronizationExport(jwt, spaceId, {
        spaceId,
        ...(mode === 'exportCTD' && { content_type: [name] }),
      });
      if (!ok) {
        throw parseResponseErrors(body, status);
      }
      if (status === 202) {
        toast.success(t('SpaceSynchronization.Export.ExportInProgress'));
      } else {
        toast.success(t('SpaceSynchronization.Export.ExportStartedMessage'));
      }
    } catch (error) {
      toast.error(
        t(
          error instanceof ResponseError
            ? 'SupportIssueModal.Form.ErrorMessage'
            : 'Form.CommunicationErrorMessage',
        ),
      );
    }
  };

  const handleSynchronize = async () => {
    if (!spaceId || !jwt) {
      return;
    }

    const handleSynchronizationSubmit = async (targetSpaceId: string) => {
      try {
        const { body, status, ok } = await synchronizationSynchronize(
          jwt,
          spaceId,
          { targetSpaceId, sourceSpaceId: spaceId },
        );

        if (!ok) {
          throw parseResponseErrors(body, status);
        }

        if (status === 202) {
          toast.success(t('SpaceSynchronization.Synchronize.SyncInProgress'));
        } else {
          toast.success(
            t('SpaceSynchronization.Synchronize.SyncStartedMessage'),
          );
        }

        return ok;
      } catch (error) {
        toast.error(
          t(
            error instanceof ResponseError
              ? 'SpaceSynchronization.Synchronize.ErrorMessage'
              : 'Form.CommunicationErrorMessage',
          ),
        );
        return false;
      }
    };

    return modal({
      id: spaceSynchronizationModalId,
      title: (
        <span className="text-3xl font-bold">
          {t('SpaceSynchronization.Synchronize.SynchronizeSpace')}
        </span>
      ),
      size: 'lg',
      content: (
        <SynchronizeSpaceModalContent
          sourceSpaceId={spaceId}
          onSubmit={handleSynchronizationSubmit}
          testId={testId}
        />
      ),
      dialogAdditionalClasses: 'text-slate-900 text-center',
      contentAdditionalClasses: 'min-h-[300px]',
    });
  };

  return {
    handleImport,
    handleExport,
    handleSynchronize,
    isSpaceAvailable,
  };
};

export default useSpaceSynchronization;
